import type { FormikProps } from 'formik'
import type { ReactNode } from 'react'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

import Yup from '@fourthwall/utils/dist/yup'

import { useCurrentShopConfigQuery } from '../../../../hooks/useCurrentShopConfigQuery'
import { useCurrentShopQuery } from '../../../../hooks/useCurrentShopQuery'
import type { PaymentMethodItem } from '../../PaymentMethods.types'
import { useShopPaymentMethods } from '../../hooks/useShopPaymentMethods'
import { useSetCurrentShopConfigChargeLimitMutation } from '../../queries/useSetCurrentShopConfigChargeLimitMutation'
import { getChargeLimitValidationSchema } from '../../utils/getChargeLimitValidationSchema'
import type {
  ChildrenProps,
  FormType,
  FormValues,
  PaymentMethodType,
} from './AddPaymentMethod.types'
import { AddPaymentMethodView } from './AddPaymentMethodView'
import { usePayPal } from './hooks/usePayPal'
import { useStripe } from './hooks/useStripe'
import { useUserPaymentMethods } from './hooks/useUserPaymentMethods'
import { useAddShopPaymentMethodMutation } from './queries/useAddShopPaymentMethodMutation'
import { useAddUserPaymentMethodMutation } from './queries/useAddUserPaymentMethodMutation'
import { useCompleteUserPaymentMethodSetupMutation } from './queries/useCompleteUserPaymentMethodSetupMutation'
import { useInitializeUserPaymentMethodPayPalSetupQuery } from './queries/useInitializeUserPaymentMethodPayPalSetupQuery'
import { useInitializeUserPaymentMethodSetupMutation } from './queries/useInitializeUserPaymentMethodSetupMutation'
import { getSetCurrentShopConfigChargeLimitPayload } from './utils/getSetCurrentShopConfigChargeLimitPayload'
import { hasChargeLimitValuesChanged } from './utils/hasChargeLimitValuesChanged'

type AddPaymentMethodContainerProps = {
  submitLabel?: string
  withChargeLimit?: boolean
  onSuccess: () => void
  onSubmit?: (paymentMethod?: PaymentMethodItem) => void
  children: (childrenProps: ChildrenProps) => ReactNode
}

export const AddPaymentMethodContainer = ({
  submitLabel = 'Save',
  withChargeLimit = true,
  onSuccess,
  onSubmit,
  children,
}: AddPaymentMethodContainerProps) => {
  const stripe = useStripe()
  const payPal = usePayPal()
  const {
    userPaymentMethods,
    selectedPaymentMethod,
    isLoadingUserPaymentMethods,
    setSelectedPaymentMethod,
  } = useUserPaymentMethods()
  const formRef = useRef<FormikProps<FormValues>>(null)
  const { currentShopQuery } = useCurrentShopQuery()
  const { shopPaymentMethods, isLoadingShopPaymentMethods } = useShopPaymentMethods()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { currentShopConfigQuery, invalidateCurrentShopConfigQuery } = useCurrentShopConfigQuery()
  const { invalidateCurrentShopQuery } = useCurrentShopQuery()
  const { completeUserPaymentMethodSetupMutation } = useCompleteUserPaymentMethodSetupMutation()
  const { addUserPaymentMethodMutation } = useAddUserPaymentMethodMutation()
  const { initializeUserPaymentMethodSetupMutation } = useInitializeUserPaymentMethodSetupMutation()
  const { initializeUserPaymentMethodPayPalSetupQuery } =
    useInitializeUserPaymentMethodPayPalSetupQuery([], { cacheTime: 0 })
  const { addShopPaymentMethodMutation } = useAddShopPaymentMethodMutation()
  const { setCurrentShopConfigChargeLimitMutation } = useSetCurrentShopConfigChargeLimitMutation()
  const isLoading =
    isLoadingUserPaymentMethods ||
    isLoadingShopPaymentMethods ||
    currentShopConfigQuery.isLoading ||
    currentShopQuery.isLoading ||
    !payPal.isInitialized

  const cardNumberRef = useRef<HTMLDivElement>(null)
  const cardExpiryRef = useRef<HTMLDivElement>(null)
  const cardCvcRef = useRef<HTMLDivElement>(null)

  const userPaymentMethodsProps = userPaymentMethods?.map((userPaymentMethod) => ({
    ...userPaymentMethod,
    onClick: () => {
      setSelectedPaymentMethod(userPaymentMethod.id)
    },
  }))
  const allFieldsInitialized = Object.values(stripe.fields).every(
    ({ isInitialized }) => isInitialized,
  )
  const allFieldsUnmounted = Object.values(stripe.fields).every(({ isMounted }) => !isMounted)

  const initialValues: FormValues = {
    formType: userPaymentMethods?.length ? 'EXISTING' : 'NEW',
    paymentMethodType: 'STRIPE',
    isPaymentMethodDefault: true,
    setChargeLimit: currentShopConfigQuery.data?.chargePolicy?.type === 'LIMITED',
    chargeLimit:
      currentShopConfigQuery.data?.chargePolicy?.type === 'LIMITED'
        ? currentShopConfigQuery.data.chargePolicy.amount.value.toString()
        : '',
  }

  const validationSchema = Yup.object().shape({
    ...(withChargeLimit ? getChargeLimitValidationSchema() : {}),
  })

  useLayoutEffect(() => {
    if (
      allFieldsInitialized &&
      allFieldsUnmounted &&
      cardNumberRef.current &&
      cardExpiryRef.current &&
      cardCvcRef.current
    ) {
      stripe.fields.cardNumber.mount(cardNumberRef.current)
      stripe.fields.cardExpiry.mount(cardExpiryRef.current)
      stripe.fields.cardCvc.mount(cardCvcRef.current)
    }
  }, [
    allFieldsInitialized,
    allFieldsUnmounted,
    isLoadingUserPaymentMethods,
    userPaymentMethodsProps,
    cardNumberRef.current,
    cardExpiryRef.current,
    cardCvcRef.current,
  ])

  useEffect(() => {
    if (
      initializeUserPaymentMethodPayPalSetupQuery.data &&
      currentShopQuery.data?.settings.payment.paypalClientId
    )
      payPal.initialize(
        currentShopQuery.data?.settings.payment.paypalClientId,
        initializeUserPaymentMethodPayPalSetupQuery.data.idToken,
      )
  }, [
    initializeUserPaymentMethodPayPalSetupQuery.data?.idToken,
    currentShopQuery.data?.settings.payment.paypalClientId,
  ])

  const setCurrentShopConfigChargeLimit = (values: FormValues) => {
    if (!hasChargeLimitValuesChanged(values, initialValues) || !withChargeLimit) {
      onSuccess()
      setIsSubmitting(false)

      return
    }

    setCurrentShopConfigChargeLimitMutation.mutate(
      [getSetCurrentShopConfigChargeLimitPayload(values)],
      {
        onSuccess: () => {
          invalidateCurrentShopConfigQuery()
          onSuccess()
        },
        onSettled: () => {
          setIsSubmitting(false)
        },
      },
    )
  }

  const addShopPaymentMethod = (
    values: FormValues,
    paymentMethodId: string,
    setDefault: boolean,
  ) => {
    addShopPaymentMethodMutation.mutate([{ paymentMethodId, setDefault }], {
      onSuccess: () => {
        const paymentMethod = userPaymentMethods?.find((pm) => pm.id === paymentMethodId)

        onSubmit?.(paymentMethod)

        setCurrentShopConfigChargeLimit(values)
        invalidateCurrentShopQuery()
      },
    })
  }

  const addNewStripeCard = async (values: FormValues) => {
    try {
      const data = await initializeUserPaymentMethodSetupMutation.mutateAsync([])
      const setupIntent = await stripe.submit(data.setupIntentClientSecret)

      if (setupIntent?.payment_method) {
        completeUserPaymentMethodSetupMutation.mutate(
          [{ paymentMethodId: setupIntent.payment_method }],
          {
            onSuccess: (data) => {
              addShopPaymentMethod(values, data.paymentMethodId, values.isPaymentMethodDefault)
            },
            onError: () => {
              setIsSubmitting(false)
            },
          },
        )
      } else {
        setIsSubmitting(false)
      }
    } catch (error) {
      setIsSubmitting(false)
    }
  }

  const handlePaymentMethodTypeChange = (
    formType: FormType,
    paymentMethodType: PaymentMethodType,
  ) => {
    if (formType === 'NEW' && paymentMethodType === 'PAYPAL') {
      payPal.renderButton({
        vaultSetupToken: initializeUserPaymentMethodPayPalSetupQuery.data?.setupToken!,
        onClick: async () => {
          await formRef?.current?.submitForm()

          return Promise.resolve(!!formRef?.current?.isValid)
        },
        onApprove: async ({ vaultSetupToken }) => {
          addUserPaymentMethodMutation.mutate(
            [{ type: 'PAYPAL', paypalSetupToken: vaultSetupToken }],
            {
              onSuccess: (data) => {
                addShopPaymentMethod(
                  formRef?.current?.values!,
                  data.paymentMethodId,
                  formRef?.current?.values?.isPaymentMethodDefault!,
                )
              },
              onError: () => {
                setIsSubmitting(false)
              },
            },
          )
        },
        onError: () => {
          setIsSubmitting(false)
          // handle error
        },
        onCancel: () => {
          setIsSubmitting(false)
        },
      })
    } else {
      payPal.destroyButton()
    }
  }

  const handleSubmit = (values: FormValues) => {
    setIsSubmitting(true)

    if (values.formType === 'EXISTING' && selectedPaymentMethod) {
      onSubmit?.(selectedPaymentMethod)
      addShopPaymentMethod(values, selectedPaymentMethod.id, values.isPaymentMethodDefault)
    }

    if (
      values.paymentMethodType === 'STRIPE' &&
      (values.formType === 'NEW' || !userPaymentMethods?.length)
    ) {
      addNewStripeCard(values)
    }

    // NOTE: we handle PayPal submit in separate handler
  }

  return (
    <AddPaymentMethodView
      formikRef={formRef}
      submitLabel={submitLabel}
      paymentMethodsProps={userPaymentMethodsProps}
      fields={{
        cardNumber: {
          error: stripe.fields.cardNumber.error,
          isFocused: stripe.fields.cardNumber.isFocused,
          ref: cardNumberRef,
        },
        cardExpiry: {
          error: stripe.fields.cardExpiry.error,
          isFocused: stripe.fields.cardExpiry.isFocused,
          ref: cardExpiryRef,
        },
        cardCvc: {
          error: stripe.fields.cardCvc.error,
          isFocused: stripe.fields.cardCvc.isFocused,
          ref: cardCvcRef,
        },
      }}
      initialValues={initialValues}
      validationSchema={validationSchema}
      shouldRenderIsPaymentMethodDefaultCheckbox={
        !isLoading && !!shopPaymentMethods && shopPaymentMethods.length > 0
      }
      shouldRenderChargeLimit={withChargeLimit}
      currentMonthSpendingValue={currentShopConfigQuery.data?.chargePolicy.chargedCurrentMonth}
      isSubmitting={isSubmitting}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      onPaymentMethodTypeChange={handlePaymentMethodTypeChange}
    >
      {children}
    </AddPaymentMethodView>
  )
}
